import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 49 — Room Level EXP Management (super_admin) over
 * routes/admin/exp.js. The list order IS the level number (row 1 = Level 1).
 * Level 1 is locked at 0 EXP; Required EXP must strictly increase. Adding,
 * editing, deleting and reordering only change this local draft until Save;
 * the server (save_room_levels) re-validates everything and replaces the
 * table atomically. Room level shown in the app is computed from this table.
 */
interface Row { key: number; requiredXp: string }
let keySeq = 1;

export function RoomLevelsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { showToast } = useToast();

  async function load() {
    setLoading(true);
    try {
      const res = await apiRequest<{ levels: { level: number; requiredXp: number }[] }>("/admin/room-levels");
      setRows(res.levels.map((l) => ({ key: keySeq++, requiredXp: String(l.requiredXp) })));
      setDirty(false);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not load room levels", "error");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function mutate(fn: (r: Row[]) => Row[]) {
    setRows((r) => fn(r));
    setDirty(true);
  }

  function problem(i: number): string | null {
    const v = rows[i].requiredXp.trim();
    if (v === "" || !/^\d+$/.test(v)) return "whole number, 0 or more";
    const n = Number(v);
    if (i === 0) return n === 0 ? null : "Level 1 must be 0";
    const prev = rows[i - 1].requiredXp.trim();
    if (/^\d+$/.test(prev) && n <= Number(prev)) return `must be greater than ${Number(prev).toLocaleString()}`;
    return null;
  }
  const hasProblem = rows.some((_, i) => problem(i) !== null);

  async function save() {
    setSaving(true);
    try {
      const res = await apiRequest<{ levels: { level: number; requiredXp: number }[] }>("/admin/room-levels", {
        method: "PATCH",
        body: { levels: rows.map((r) => ({ requiredXp: Number(r.requiredXp) })) },
      });
      setRows(res.levels.map((l) => ({ key: keySeq++, requiredXp: String(l.requiredXp) })));
      setDirty(false);
      showToast("Room levels saved", "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setSaving(false);
    }
  }

  function addLevel() {
    mutate((r) => {
      const last = r.length ? Number(r[r.length - 1].requiredXp) || 0 : -1;
      return [...r, { key: keySeq++, requiredXp: String(last + 1000 > 0 ? last + 1000 : 0) }];
    });
  }

  function move(i: number, d: -1 | 1) {
    mutate((r) => {
      const j = i + d;
      if (j < 0 || j >= r.length) return r;
      const c = [...r];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  }

  return (
    <AppShell title="Room Level EXP">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        A room reaches Level N once its Room EXP is at least that level's Required EXP. The row order is the level
        number. Level 1 must be 0 and every next level must need more EXP than the one above it. Deleting or moving a
        level renumbers the levels after it, which can change the level shown for existing rooms; nothing applies
        until you press Save, and the server re-checks the whole list.
      </p>
      <Card className="max-w-2xl p-5">
        {loading ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => {
              const p = problem(i);
              return (
                <div key={r.key} className="flex items-start gap-2">
                  <span className="mt-6 w-20 shrink-0 text-sm font-semibold text-ink">Level {i + 1}</span>
                  <div className="flex-1">
                    <Input
                      label={i === 0 ? "Required EXP" : undefined}
                      type="number"
                      min={0}
                      value={r.requiredXp}
                      disabled={i === 0}
                      onChange={(e) => mutate((all) => all.map((x) => (x.key === r.key ? { ...x, requiredXp: e.target.value } : x)))}
                    />
                    {p && <p className="mt-0.5 text-xs text-danger">{p}</p>}
                  </div>
                  <div className={`flex gap-1 ${i === 0 ? "mt-6" : "mt-6"}`}>
                    <Button variant="secondary" onClick={() => move(i, -1)} disabled={i === 0 || i === 1}>↑</Button>
                    <Button variant="secondary" onClick={() => move(i, 1)} disabled={i === 0 || i === rows.length - 1}>↓</Button>
                    <Button variant="danger" onClick={() => mutate((all) => all.filter((x) => x.key !== r.key))} disabled={i === 0 || rows.length <= 1}>
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className="flex gap-2 border-t border-surface-border pt-4">
              <Button variant="secondary" onClick={addLevel}>Add level</Button>
              <Button onClick={save} isLoading={saving} disabled={!dirty || hasProblem || !rows.length}>Save</Button>
              <Button variant="secondary" onClick={() => void load()} disabled={saving || !dirty}>Discard changes</Button>
            </div>
          </div>
        )}
      </Card>
    </AppShell>
  );
}
